import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Mail,
  MailOpen,
  ExternalLink,
  ClipboardList,
  ChevronRight,
  ChevronDown,
  Video,
  Star,
  User,
  MapPin,
  Clock,
  CalendarDays,
  Phone,
  Link2,
  Send,
  Reply,
  MessageSquare,
  CheckCircle,
  XCircle,
  HelpCircle,
  Eye,
  Zap,
  AlertCircle,
  Inbox,
  X,
  Pencil,
} from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import type { Application, ApplicationStatusHistory, EmailThread, CTAAction } from '@core/types';
import { applicationService } from '@core/supabase/services/applicationService';
import {
  interviewEventService,
  type InterviewEvent,
  type InterviewEventWithApplication,
} from '@core/supabase/services/interviewEventService';
import { emailService } from '@core/supabase/services/emailService';
import { ALL_STATUSES, PIPELINE_STATUS_LABEL } from '@core/constants/pipelineStages';
import type { ApplicationStatus } from '@core/types';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { StatusBadge } from '@shared/components/ui/StatusBadge';
import {
  buildApplicationHeartbeatTimeline,
  ApplicationHeartbeatTimeline,
} from '@modules/applications/applicationHeartbeatTimeline';
import { CandidateCollapsiblePanel } from '@modules/candidates/CandidateCollapsiblePanel';
import { CandidateApplicationDetailModal } from '@modules/candidates/CandidateApplicationDetailModal';
import { InterviewMonthCalendar, type MonthCalendarEvent } from './InterviewMonthCalendar';
import { InterviewWizardModal } from './InterviewWizardModal';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';

/* ─── Constants ─────────────────────────────────────────────────── */
const BRAND = '#8B80F9';
const LIFECYCLE_LABELS = ['Draft', 'Self-book', 'Invite sent', 'Scheduled', 'Closed'] as const;

const CTA_ICON: Record<CTAAction, React.ReactNode> = {
  accept: <CheckCircle className="w-3.5 h-3.5 text-green-400" />,
  reject: <XCircle className="w-3.5 h-3.5 text-red-400" />,
  query:  <HelpCircle className="w-3.5 h-3.5 text-yellow-400" />,
};
const CTA_LABEL: Record<CTAAction, string> = { accept: 'Shortlisted', reject: 'Passed', query: 'Query' };

function formatTimelineDate(date: string) {
  return formatDateTimeGmt(date);
}
const VALID_TABS = ['overview', 'candidate', 'hiring', 'interview', 'email', 'parameters'] as const;
type DetailTab = (typeof VALID_TABS)[number];

const TABS: { id: DetailTab; label: string }[] = [
  { id: 'overview',   label: 'Overview'      },
  { id: 'candidate',  label: 'Candidate'     },
  { id: 'hiring',     label: 'Hiring Details'},
  { id: 'interview',  label: 'Interview'     },
  { id: 'email',      label: 'Email'         },
  { id: 'parameters', label: 'Parameters'    },
];

/** Shared dt/dd styles for Parameters tab — mirrors RequisitionDetailPage */
const PARAM_DT = 'vscode-text-secondary pr-2 min-w-0';
const PARAM_DD = 'vscode-text-primary min-w-0 break-words';

function refLabel(id: string) {
  return `APP-${id.replace(/-/g, '').slice(0, 8).toUpperCase()}`;
}

function fmtDateLong(iso: string | null | undefined): string {
  return formatDateTimeGmt(iso);
}

/* ─── Helpers ────────────────────────────────────────────────────── */
function lifecyclePhaseIndex(ev: InterviewEvent): number {
  if (ev.status === 'conducted' || ev.status === 'cancelled' || ev.status === 'no_show') return 4;
  if (ev.status === 'scheduled') return 3;
  if (ev.invite_sent_at) return 2;
  if (ev.status === 'awaiting_booking') return 1;
  return 0;
}

function statusRank(s: ApplicationStatus): number {
  const i = ALL_STATUSES.indexOf(s);
  return i >= 0 ? i : 0;
}

function fmt(iso: string | null | undefined): string {
  const s = formatDateTimeGmt(iso);
  return s === '—' && iso ? iso : s;
}

function fmtDate(iso: string | null | undefined): string {
  return formatDateTimeGmt(iso);
}

function toLocalDayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* ─── Shared UI atoms ────────────────────────────────────────────── */

/** Attribute row inside a ggj-attr-grid */
function AttrRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="vscode-text-secondary pr-2 min-w-0">{label}</dt>
      <dd className="vscode-text-primary min-w-0 break-words">{children}</dd>
    </>
  );
}

/** Consistent attribute group — same purple tint header throughout, matching Jobs page */
function AttrGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="ggj-param-group">
      <div className="ggj-profile-section-h">{title}</div>
      <div className="ggj-param-group-body">{children}</div>
    </div>
  );
}

/** Pill badge for interview round status */
function RoundStatusBadge({ status }: { status: string }) {
  const cfg: Record<string, { bg: string; color: string }> = {
    conducted:       { bg: 'rgba(34,197,94,0.12)',   color: '#22c55e' },
    scheduled:       { bg: `${BRAND}18`,             color: BRAND },
    awaiting_booking:{ bg: 'rgba(245,158,11,0.12)',  color: '#f59e0b' },
    no_show:         { bg: 'rgba(239,68,68,0.12)',   color: '#ef4444' },
    cancelled:       { bg: 'rgba(100,116,139,0.15)', color: '#94a3b8' },
    draft:           { bg: 'rgba(100,116,139,0.10)', color: '#94a3b8' },
  };
  const c = cfg[status] ?? { bg: 'rgba(100,116,139,0.10)', color: '#94a3b8' };
  return (
    <span
      className="text-xs px-2 py-0.5 rounded-full font-medium capitalize inline-block"
      style={{ background: c.bg, color: c.color }}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

/* ─── Page ───────────────────────────────────────────────────────── */
export function InterviewCandidateDetailPage() {
  const { candidateId } = useParams<{ candidateId: string }>();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [apps, setApps] = useState<Application[]>([]);
  const [eventsByApp, setEventsByApp] = useState<Record<string, InterviewEvent[]>>({});
  const [listLoading, setListLoading] = useState(true);

  const [detailApp, setDetailApp] = useState<Application | null>(null);
  const [history, setHistory] = useState<ApplicationStatusHistory[]>([]);
  const [emailThreads, setEmailThreads] = useState<EmailThread[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const [monthAnchor, setMonthAnchor] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardScheduleDayKey, setWizardScheduleDayKey] = useState<string | null>(null);
  const [emailDetailThread, setEmailDetailThread] = useState<EmailThread | null>(null);
  const [emailAccordionOpen, setEmailAccordionOpen] = useState<Record<string, boolean>>({ body: true, cta: false, track: false, replies: false });
  const [appModal, setAppModal] = useState<Application | null>(null);

  /* ── Data loading ── */
  const loadList = useCallback(async () => {
    if (!candidateId || !user?.orgId) return;
    const [list, orgEvents] = await Promise.all([
      applicationService.list(user.orgId, { candidateId, limit: 200 }),
      interviewEventService.listForOrgWithApplications(user.orgId, { limit: 2000 }),
    ]);
    const sorted = Array.isArray(list)
      ? [...list].sort((a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime())
      : [];
    setApps(sorted);
    const idSet = new Set(sorted.map((a) => a.id));
    const evMap: Record<string, InterviewEvent[]> = {};
    for (const a of sorted) evMap[a.id] = [];
    for (const ev of orgEvents) {
      if (!idSet.has(ev.application_id)) continue;
      const { application: _a, ...row } = ev as InterviewEventWithApplication;
      evMap[ev.application_id]!.push(row);
    }
    for (const id of idSet) {
      evMap[id]!.sort((x, y) => x.round - y.round || new Date(x.created_at).getTime() - new Date(y.created_at).getTime());
    }
    setEventsByApp(evMap);
  }, [candidateId, user?.orgId]);

  useEffect(() => {
    if (!candidateId || !user?.orgId) return;
    setListLoading(true);
    loadList().catch(() => { setApps([]); setEventsByApp({}); }).finally(() => setListLoading(false));
  }, [candidateId, user?.orgId, loadList]);

  const selectedApplicationId = useMemo(() => {
    if (apps.length === 0) return '';
    const q = searchParams.get('applicationId');
    if (q && apps.some((a) => a.id === q)) return q;
    return apps[0]!.id;
  }, [apps, searchParams]);

  useEffect(() => {
    if (apps.length === 0) return;
    const q = searchParams.get('applicationId');
    if (!q || !apps.some((a) => a.id === q)) {
      const next = new URLSearchParams(searchParams);
      next.set('applicationId', apps[0]!.id);
      setSearchParams(next, { replace: true });
    }
  }, [apps, searchParams, setSearchParams]);

  useEffect(() => {
    if (!selectedApplicationId || !user?.orgId) return;
    let cancelled = false;
    setDetailLoading(true);
    Promise.all([
      applicationService.getById(selectedApplicationId),
      applicationService.getStatusHistory(selectedApplicationId),
      emailService.listByApplication(selectedApplicationId),
    ])
      .then(([row, h, threads]) => {
        if (cancelled) return;
        setDetailApp(row); setHistory(h ?? []); setEmailThreads(threads ?? []);
      })
      .catch(() => { if (!cancelled) { setDetailApp(null); setHistory([]); setEmailThreads([]); } })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selectedApplicationId, user?.orgId]);

  /* ── Tab state ── */
  const rawTab = searchParams.get('tab');
  const activeTab: DetailTab = VALID_TABS.includes(rawTab as DetailTab) ? (rawTab as DetailTab) : 'overview';

  const setTab = (t: DetailTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const appId = next.get('applicationId') || selectedApplicationId;
      if (appId) next.set('applicationId', appId);
      if (t === 'overview') next.delete('tab'); else next.set('tab', t);
      return next;
    }, { replace: true });
  };

  /* ── Derived ── */
  const { timeline, nextActionIndex } = useMemo(
    () => buildApplicationHeartbeatTimeline(history, emailThreads),
    [history, emailThreads]
  );

  const eventsForSelected = selectedApplicationId ? eventsByApp[selectedApplicationId] ?? [] : [];

  const sortedEvents = useMemo(
    () => [...eventsForSelected].sort((a, b) => {
      const ta = a.scheduled_at ? new Date(a.scheduled_at).getTime() : new Date(a.created_at).getTime();
      const tb = b.scheduled_at ? new Date(b.scheduled_at).getTime() : new Date(b.created_at).getTime();
      return ta - tb;
    }),
    [eventsForSelected]
  );

  const monthEvents: MonthCalendarEvent[] = useMemo(() => {
    const start = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
    const end   = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 0, 23, 59, 59, 999);
    return eventsForSelected
      .filter((r) => r.scheduled_at)
      .filter((r) => { const t = new Date(r.scheduled_at!); return t >= start && t <= end; })
      .map((r) => ({ id: r.id, scheduled_at: r.scheduled_at!, label: `R${r.round}` }));
  }, [eventsForSelected, monthAnchor]);

  const primaryEvent = useMemo(() => {
    if (eventsForSelected.length === 0) return null;
    const now = Date.now();
    const upcoming = eventsForSelected
      .filter((e) => e.scheduled_at && new Date(e.scheduled_at).getTime() >= now)
      .sort((a, b) => new Date(a.scheduled_at!).getTime() - new Date(b.scheduled_at!).getTime())[0];
    return upcoming ?? [...eventsForSelected].sort((a, b) => b.round - a.round)[0] ?? null;
  }, [eventsForSelected]);

  const phaseIdx = primaryEvent ? lifecyclePhaseIndex(primaryEvent) : -1;

  const nextScheduled = useMemo(() => {
    const now = Date.now();
    return eventsForSelected
      .filter((e) => e.scheduled_at && new Date(e.scheduled_at).getTime() >= now)
      .sort((a, b) => new Date(a.scheduled_at!).getTime() - new Date(b.scheduled_at!).getTime())[0]
      ?.scheduled_at ?? null;
  }, [eventsForSelected]);

  const furthestLabel = useMemo(() => {
    if (apps.length === 0) return '—';
    const best = apps.reduce((acc, a) => (statusRank(a.status) > statusRank(acc.status) ? a : acc), apps[0]!);
    return PIPELINE_STATUS_LABEL[best.status];
  }, [apps]);

  const contextQuickLinks = useMemo(() => {
    if (!detailApp || detailApp.id !== selectedApplicationId)
      return [{ key: 'hub', to: '/interviews', label: 'Interviews list' }];
    const id = detailApp.id;
    const c  = detailApp.candidate ?? apps[0]?.candidate;
    const j  = detailApp.job;
    const cl = detailApp.client;
    const prep = c?.id && j?.id ? `/interviews/prep?candidateId=${c.id}&jobId=${j.id}` : '/interviews/prep';
    switch (activeTab) {
      case 'overview':
        return [
          ...(c?.id ? [{ key: 'cand', to: `/candidates/${c.id}`, label: c.full_name ?? 'Candidate' }] : []),
          ...(j?.id ? [{ key: 'job',  to: `/jobs/${j.id}`,       label: `${j.title ?? 'Job'} @ ${cl?.name ?? ''}` }] : []),
          { key: 'app',  to: `/applications/${id}`,               label: 'Application (pipeline & forms)' },
          { key: 'mail', to: `/emails/compose?applicationId=${id}`, label: 'Compose email' },
          { key: 'prep', to: prep,                                label: 'Prep & scorecards' },
        ];
      case 'candidate':
        return [
          ...(c?.id ? [{ key: 'cand', to: `/candidates/${c.id}`, label: 'Full candidate profile' }] : []),
          { key: 'app', to: `/applications/${id}`, label: 'This application' },
          { key: 'hub', to: '/interviews',          label: 'Interviews hub' },
        ];
      case 'hiring':
        return [
          ...(j?.id  ? [{ key: 'job', to: `/jobs/${j.id}`,       label: j.title ?? 'Job posting' }]   : []),
          ...(cl?.id ? [{ key: 'cli', to: `/clients/${cl.id}`,   label: cl.name ?? 'Client' }]         : []),
          { key: 'app', to: `/applications/${id}`, label: 'Full application' },
        ];
      case 'interview':
        return [
          { key: 'prep',   to: prep,                                       label: 'Prep workspace' },
          { key: 'appint', to: `/applications/${id}?tab=interviews`,       label: 'Application · Interviews' },
          { key: 'hub',    to: '/interviews',                              label: 'All interviews' },
          ...(c?.id ? [{ key: 'cand', to: `/candidates/${c.id}`,          label: 'Candidate profile' }] : []),
        ];
      case 'email':
        return [
          { key: 'cmp',  to: `/emails/compose?applicationId=${id}`, label: 'Compose email' },
          { key: 'inb',  to: '/emails',                              label: 'Email inbox' },
          { key: 'appe', to: `/applications/${id}?tab=emails`,       label: 'Application · Emails' },
        ];
      case 'parameters':
        return [
          { key: 'app',  to: `/applications/${id}`,                  label: 'Full Application'  },
          { key: 'hub',  to: '/interviews',                           label: 'Interviews hub'    },
          ...(c?.id  ? [{ key: 'cand', to: `/candidates/${c.id}`,    label: 'Candidate profile' }] : []),
          ...(j?.id  ? [{ key: 'job',  to: `/jobs/${j.id}`,          label: j.title ?? 'Job'    }] : []),
          ...(cl?.id ? [{ key: 'cli',  to: `/clients/${cl.id}`,      label: cl.name ?? 'Client' }] : []),
        ];
      default:
        return [];
    }
  }, [activeTab, detailApp, selectedApplicationId, apps]);

  /* ── Wizard ── */
  const openWizardNew  = () => { setWizardScheduleDayKey(null); setWizardOpen(true); };
  const openWizardForDay = (day: Date) => { setWizardScheduleDayKey(toLocalDayKey(day)); setWizardOpen(true); };
  const closeWizard    = () => { setWizardOpen(false); setWizardScheduleDayKey(null); };

  /* ── Derived refs ── */
  const cand   = detailApp?.candidate ?? apps[0]?.candidate;
  const job    = detailApp?.job;
  const client = detailApp?.client;
  const prepHref = cand?.id && job?.id ? `/interviews/prep?candidateId=${cand.id}&jobId=${job.id}` : '/interviews/prep';

  if (!candidateId) return <p className="vscode-text-secondary">Missing candidate ID.</p>;
  if (listLoading)  return <FullPageSpinner />;
  if (!user?.orgId || apps.length === 0) {
    return (
      <div className="space-y-3 p-4">
        <p className="vscode-text-secondary">No applications found for this candidate in your org.</p>
        <Link to="/interviews" className="text-brand-400 hover:underline text-sm">← Back to Interviews</Link>
      </div>
    );
  }

  const appReady = Boolean(detailApp && detailApp.id === selectedApplicationId && detailApp.org_id === user.orgId);

  /* ─────────────────────────────────────────────────────────────── */
  return (
    <div className="w-full pb-8">

      {/* Page header */}
      <PageHeader
        icon={Video}
        breadcrumb={[
          { label: 'Interviews', href: '/interviews' },
          { label: cand?.full_name ?? 'Candidate' },
        ]}
        title={cand?.full_name ?? 'Candidate'}
        subtitle={`${apps.length} role${apps.length !== 1 ? 's' : ''} · furthest stage: ${furthestLabel}`}
      />

      {/* Tabs */}
      <nav className="ggj-cand-tablist mb-0">
        {TABS.map((t) => {
          const count = t.id === 'interview' ? eventsForSelected.length : t.id === 'email' ? emailThreads.length : 0;
          return (
            <button key={t.id} type="button"
              className={`ggj-cand-tab${activeTab === t.id ? ' ggj-cand-tab--active' : ''}`}
              onClick={() => setTab(t.id)}>
              {t.label}{count > 0 ? ` (${count})` : ''}
            </button>
          );
        })}
      </nav>

      {/* Toolbar — context actions */}
      <div className="ggj-profile-toolbar mb-5 flex-wrap gap-y-1">

        {activeTab === 'overview' && appReady && (
          <Link to={`/emails/compose?applicationId=${detailApp!.id}`}
            className="ggj-toolbar-btn inline-flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5" /> Send Email
          </Link>
        )}
        {activeTab === 'interview' && (
          <>
            <button type="button" className="ggj-toolbar-btn inline-flex items-center gap-1.5" onClick={openWizardNew}>
              <Plus className="w-3.5 h-3.5" /> New Interview
            </button>
            <span className="vscode-text-tertiary text-xs select-none">·</span>
            <Link to={prepHref} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
              <ClipboardList className="w-3.5 h-3.5" /> Prep Workspace
            </Link>
          </>
        )}
        {activeTab === 'email' && appReady && (
          <>
            <Link to={`/emails/compose?applicationId=${detailApp!.id}`}
              className="ggj-toolbar-btn inline-flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> New Email
            </Link>
            {cand?.email && (
              <>
                <span className="vscode-text-tertiary text-xs select-none">·</span>
                <a href={`mailto:${cand.email}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" /> Email Candidate
                </a>
              </>
            )}
            {client?.contact_email && (
              <>
                <span className="vscode-text-tertiary text-xs select-none">·</span>
                <a href={`mailto:${client.contact_email}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                  <Send className="w-3.5 h-3.5" /> Email Client
                </a>
              </>
            )}
          </>
        )}
        {activeTab === 'candidate' && cand?.id && (
          <Link to={`/candidates/${cand.id}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
            <ExternalLink className="w-3.5 h-3.5" /> Open Candidate Record
          </Link>
        )}
        {activeTab === 'hiring' && appReady && (
          <>
            <Link to={`/applications/${detailApp!.id}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
              <ExternalLink className="w-3.5 h-3.5" /> Full Application
            </Link>
            {detailApp!.job?.id && (
              <>
                <span className="vscode-text-tertiary text-xs select-none">·</span>
                <Link to={`/jobs/${detailApp!.job.id}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                  <ExternalLink className="w-3.5 h-3.5" /> View Job
                </Link>
              </>
            )}
          </>
        )}
        {activeTab === 'parameters' && appReady && detailApp && (
          <Link to={`/applications/${detailApp.id}?edit=1`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
            <Pencil className="w-3.5 h-3.5" aria-hidden /> Edit application
          </Link>
        )}
      </div>

      {/* Main grid — two-column for most tabs, full-width for Parameters */}
      <div className={`grid grid-cols-1 gap-6 ${activeTab !== 'parameters' ? 'lg:grid-cols-[1fr_340px]' : ''}`}>

        {/* ── LEFT: tab content ── */}
        <div className="min-w-0 space-y-4">
          {detailLoading && !appReady ? (
            <div className="py-16 flex justify-center"><FullPageSpinner /></div>
          ) : !appReady ? (
            <p className="vscode-text-tertiary text-sm py-4">Could not load this application.</p>
          ) : (
            <>

              {/* ═══════ OVERVIEW TAB ═══════ */}
              {activeTab === 'overview' && (
                <div className="space-y-4">

                  <AttrGroup title="Pipeline Status">
                    <div className="mb-3">
                      <StatusBadge status={detailApp!.status} />
                    </div>
                    <dl className="ggj-attr-grid text-sm">
                      <AttrRow label="Pipeline stage">{PIPELINE_STATUS_LABEL[detailApp!.status]}</AttrRow>
                      <AttrRow label="Submitted">{detailApp!.submitted_at ? formatDateTimeGmt(detailApp!.submitted_at) : '—'}</AttrRow>
                      <AttrRow label="Assigned to">{detailApp!.assigned_user?.display_name ?? detailApp!.assigned_user?.email ?? '—'}</AttrRow>
                      <AttrRow label="Source">{detailApp!.source ?? '—'}</AttrRow>
                      <AttrRow label="Fitment score">
                        {detailApp!.fitment_score != null
                          ? <span className="inline-flex items-center gap-1"><Star className="w-3.5 h-3.5 text-amber-400" />{detailApp!.fitment_score}</span>
                          : '—'}
                      </AttrRow>
                      <AttrRow label="Last updated">{detailApp!.updated_at ? formatDateTimeGmt(detailApp!.updated_at) : '—'}</AttrRow>
                    </dl>
                  </AttrGroup>

                  <AttrGroup title="Interview Overview">
                    <dl className="ggj-attr-grid text-sm">
                      <AttrRow label="Total rounds">{eventsForSelected.length || '—'}</AttrRow>
                      <AttrRow label="Rounds conducted">{eventsForSelected.filter(e => e.status === 'conducted').length || '—'}</AttrRow>
                      <AttrRow label="Rounds scheduled">{eventsForSelected.filter(e => e.status === 'scheduled').length || '—'}</AttrRow>
                      <AttrRow label="Next scheduled">{fmt(nextScheduled)}</AttrRow>
                      <AttrRow label="Latest round">
                        {primaryEvent ? `Round ${primaryEvent.round}` : '—'}
                      </AttrRow>
                      <AttrRow label="Latest round status">
                        {primaryEvent ? <RoundStatusBadge status={primaryEvent.status} /> : '—'}
                      </AttrRow>
                      <AttrRow label="Lead interviewer">{primaryEvent?.interviewer_name ?? '—'}</AttrRow>
                      <AttrRow label="Lead email">{primaryEvent?.interviewer_email ?? '—'}</AttrRow>
                      <AttrRow label="Location / link">{primaryEvent?.location ?? '—'}</AttrRow>
                      <AttrRow label="Duration">{primaryEvent?.duration_minutes != null ? `${primaryEvent.duration_minutes} min` : '—'}</AttrRow>
                      <AttrRow label="Invite sent">{fmt(primaryEvent?.invite_sent_at)}</AttrRow>
                      <AttrRow label="Panelists">
                        {primaryEvent?.attendees?.length
                          ? primaryEvent.attendees.map(a => a.name).filter(Boolean).join(', ')
                          : '—'}
                      </AttrRow>
                    </dl>
                  </AttrGroup>

                  {(primaryEvent?.scheduling_meta?.candidateAvailability || primaryEvent?.scheduling_meta?.panelAvailability || primaryEvent?.notes) && (
                    <AttrGroup title="Scheduling Context">
                      <dl className="ggj-attr-grid text-sm">
                        {primaryEvent?.scheduling_meta?.candidateAvailability && (
                          <AttrRow label="Candidate availability">{primaryEvent.scheduling_meta.candidateAvailability}</AttrRow>
                        )}
                        {primaryEvent?.scheduling_meta?.panelAvailability && (
                          <AttrRow label="Panel availability">{primaryEvent.scheduling_meta.panelAvailability}</AttrRow>
                        )}
                        {primaryEvent?.notes && (
                          <AttrRow label="Internal notes">{primaryEvent.notes}</AttrRow>
                        )}
                      </dl>
                    </AttrGroup>
                  )}

                  {detailApp!.internal_notes && (
                    <AttrGroup title="Application Notes">
                      <p className="text-sm vscode-text-secondary whitespace-pre-wrap leading-relaxed">
                        {detailApp!.internal_notes}
                      </p>
                    </AttrGroup>
                  )}
                </div>
              )}

              {/* ═══════ CANDIDATE TAB ═══════ */}
              {activeTab === 'candidate' && (
                <div className="space-y-4">

                  {/* Submitted Applications — all roles for this candidate */}
                  <AttrGroup title="Submitted Applications">
                    <dl className="ggj-attr-grid text-sm">
                      <AttrRow label="Submitted applications">
                        <div className="space-y-1">
                          {apps.map((a) => (
                            <button key={a.id} type="button"
                              onClick={() => setAppModal(a)}
                              className="block text-brand-400 hover:text-brand-300 hover:underline text-left w-full">
                              {a.job?.title ?? 'Unknown role'}
                              {a.client?.name && <span className="vscode-text-tertiary text-xs"> @ {a.client.name}</span>}
                              <span className="ml-2 text-[10px] opacity-70">— {PIPELINE_STATUS_LABEL[a.status]}</span>
                            </button>
                          ))}
                        </div>
                      </AttrRow>
                    </dl>
                  </AttrGroup>

                  <AttrGroup title="Personal Details">
                    <dl className="ggj-attr-grid text-sm">
                      <AttrRow label="Full name">{cand?.full_name ?? '—'}</AttrRow>
                      <AttrRow label="Email">
                        {cand?.email
                          ? <a href={`mailto:${cand.email}`} className="text-brand-400 hover:underline">{cand.email}</a>
                          : '—'}
                      </AttrRow>
                      <AttrRow label="Phone">
                        {cand?.phone
                          ? <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3 opacity-50" />{cand.phone}</span>
                          : '—'}
                      </AttrRow>
                      <AttrRow label="Location">
                        {cand?.location
                          ? <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 opacity-50" />{cand.location}</span>
                          : '—'}
                      </AttrRow>
                      <AttrRow label="Source">{cand?.source ?? '—'}</AttrRow>
                      <AttrRow label="LinkedIn">
                        {cand?.linkedin_url
                          ? <a href={cand.linkedin_url} target="_blank" rel="noreferrer" className="text-brand-400 hover:underline inline-flex items-center gap-1">View <ExternalLink className="w-3 h-3" /></a>
                          : '—'}
                      </AttrRow>
                      <AttrRow label="Created">{fmtDate(cand?.created_at)}</AttrRow>
                      <AttrRow label="Last updated">{fmtDate(cand?.updated_at)}</AttrRow>
                    </dl>
                  </AttrGroup>

                  <AttrGroup title="Professional Background">
                    <dl className="ggj-attr-grid text-sm">
                      <AttrRow label="Current title">{cand?.current_title ?? '—'}</AttrRow>
                      <AttrRow label="Current company">{cand?.current_company ?? '—'}</AttrRow>
                      <AttrRow label="Years experience">{cand?.years_experience != null ? `${cand.years_experience} yrs` : '—'}</AttrRow>
                      <AttrRow label="Notice period">{cand?.notice_period_days != null ? `${cand.notice_period_days} days` : '—'}</AttrRow>
                      <AttrRow label="Expected salary">{cand?.expected_salary ?? '—'}</AttrRow>
                      <AttrRow label="Resume">
                        {cand?.resume_url
                          ? <a href={cand.resume_url} target="_blank" rel="noreferrer" className="text-brand-400 hover:underline inline-flex items-center gap-1">Download <ExternalLink className="w-3 h-3" /></a>
                          : '—'}
                      </AttrRow>
                    </dl>
                  </AttrGroup>

                  {cand?.parsed_skills && cand.parsed_skills.length > 0 && (
                    <AttrGroup title="Skills">
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {cand.parsed_skills.map((s) => (
                          <span key={s}
                            className="px-2.5 py-1 rounded-full text-xs font-medium"
                            style={{ background: `${BRAND}18`, color: BRAND, border: `1px solid ${BRAND}33` }}>
                            {s}
                          </span>
                        ))}
                      </div>
                    </AttrGroup>
                  )}

                  {cand?.summary && (
                    <AttrGroup title="Summary / Bio">
                      <p className="text-sm vscode-text-secondary whitespace-pre-wrap leading-relaxed">{cand.summary}</p>
                    </AttrGroup>
                  )}

                  <AttrGroup title="Application — This Role">
                    <dl className="ggj-attr-grid text-sm">
                      <AttrRow label="Pipeline stage">{PIPELINE_STATUS_LABEL[detailApp!.status]}</AttrRow>
                      <AttrRow label="Submitted">{detailApp!.submitted_at ? formatDateTimeGmt(detailApp!.submitted_at) : '—'}</AttrRow>
                      <AttrRow label="Assigned to">{detailApp!.assigned_user?.display_name ?? detailApp!.assigned_user?.email ?? '—'}</AttrRow>
                      <AttrRow label="Fitment score">
                        {detailApp!.fitment_score != null
                          ? <span className="inline-flex items-center gap-1"><Star className="w-3.5 h-3.5 text-amber-400" />{detailApp!.fitment_score}</span>
                          : '—'}
                      </AttrRow>
                    </dl>
                  </AttrGroup>
                </div>
              )}

              {/* ═══════ HIRING DETAILS TAB ═══════ */}
              {activeTab === 'hiring' && (
                <div className="space-y-4">

                  <AttrGroup title="Job Details">
                    <dl className="ggj-attr-grid text-sm">
                      <AttrRow label="Job title">
                        {job?.id
                          ? <Link to={`/jobs/${job.id}`} className="text-brand-400 hover:underline">{job.title}</Link>
                          : '—'}
                      </AttrRow>
                      <AttrRow label="Status"><span className="capitalize">{job?.status ?? '—'}</span></AttrRow>
                      <AttrRow label="Employment type">{job?.employment_type ?? '—'}</AttrRow>
                      <AttrRow label="Department">{job?.department ?? '—'}</AttrRow>
                      <AttrRow label="Location">{job?.location ?? '—'}</AttrRow>
                      <AttrRow label="Min experience">{job?.min_experience_years != null ? `${job.min_experience_years} yrs` : '—'}</AttrRow>
                      <AttrRow label="Max experience">{(job as Record<string, unknown>)?.max_experience_years != null ? `${(job as Record<string, unknown>).max_experience_years} yrs` : '—'}</AttrRow>
                      <AttrRow label="Salary range">{job?.salary_range ?? '—'}</AttrRow>
                      <AttrRow label="JD URL">
                        {job?.jd_url
                          ? <a href={job.jd_url} target="_blank" rel="noreferrer" className="text-brand-400 hover:underline inline-flex items-center gap-1">View JD <ExternalLink className="w-3 h-3" /></a>
                          : '—'}
                      </AttrRow>
                      <AttrRow label="Created">{fmtDate(job?.created_at)}</AttrRow>
                    </dl>
                  </AttrGroup>

                  {job?.skills_required && job.skills_required.length > 0 && (
                    <AttrGroup title="Required Skills">
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {job.skills_required.map((s) => (
                          <span key={s}
                            className="px-2.5 py-1 rounded-full text-xs font-medium"
                            style={{ background: `${BRAND}18`, color: BRAND, border: `1px solid ${BRAND}33` }}>
                            {s}
                          </span>
                        ))}
                      </div>
                    </AttrGroup>
                  )}

                  <AttrGroup title="Client / Employer">
                    <dl className="ggj-attr-grid text-sm">
                      <AttrRow label="Client name">
                        {client?.id
                          ? <Link to={`/clients/${client.id}`} className="text-brand-400 hover:underline">{client.name}</Link>
                          : '—'}
                      </AttrRow>
                      <AttrRow label="Industry">{client?.industry ?? '—'}</AttrRow>
                      <AttrRow label="Contact name">{client?.contact_name ?? '—'}</AttrRow>
                      <AttrRow label="Contact email">
                        {client?.contact_email
                          ? <a href={`mailto:${client.contact_email}`} className="text-brand-400 hover:underline">{client.contact_email}</a>
                          : '—'}
                      </AttrRow>
                      <AttrRow label="Contact phone">{client?.contact_phone ?? '—'}</AttrRow>
                      <AttrRow label="Website">{client?.website ? (
                        <a href={client.website} target="_blank" rel="noreferrer" className="text-brand-400 hover:underline inline-flex items-center gap-1">Visit <ExternalLink className="w-3 h-3" /></a>
                      ) : '—'}</AttrRow>
                      <AttrRow label="Location">{client?.location ?? '—'}</AttrRow>
                      <AttrRow label="Client since">{fmtDate(client?.created_at)}</AttrRow>
                    </dl>
                  </AttrGroup>

                  <AttrGroup title="Application Assignment">
                    <dl className="ggj-attr-grid text-sm">
                      <AttrRow label="Submitted">{detailApp!.submitted_at ? formatDateTimeGmt(detailApp!.submitted_at) : '—'}</AttrRow>
                      <AttrRow label="Assigned to">{detailApp!.assigned_user?.display_name ?? detailApp!.assigned_user?.email ?? '—'}</AttrRow>
                      <AttrRow label="Current stage">{PIPELINE_STATUS_LABEL[detailApp!.status]}</AttrRow>
                      <AttrRow label="Source">{detailApp!.source ?? '—'}</AttrRow>
                      <AttrRow label="Last updated">{fmtDate(detailApp!.updated_at)}</AttrRow>
                    </dl>
                  </AttrGroup>
                </div>
              )}

              {/* ═══════ INTERVIEW TAB ═══════ */}
              {activeTab === 'interview' && (
                <div className="space-y-4">

                  {sortedEvents.length === 0 ? (
                    <AttrGroup title="Interview Rounds">
                      <p className="text-sm vscode-text-tertiary py-2">
                        No interview rounds scheduled yet. Click <strong className="vscode-text-secondary">New Interview</strong> above or pick a day in the calendar below.
                      </p>
                    </AttrGroup>
                  ) : (
                    <AttrGroup title="Interview Rounds">
                      <div className="overflow-x-auto -mx-[1.15rem] -my-[1.2rem]">
                        <table className="ggj-data-table w-full">
                          <thead>
                            <tr className="border-b vscode-border text-left">
                              <th className="px-3 py-2 whitespace-nowrap">Round</th>
                              <th className="px-3 py-2">Scheduled At</th>
                              <th className="px-3 py-2">Status</th>
                              <th className="px-3 py-2 hidden sm:table-cell">Duration</th>
                              <th className="px-3 py-2 hidden sm:table-cell">Lead Interviewer</th>
                              <th className="px-3 py-2 hidden md:table-cell">Location</th>
                              <th className="px-3 py-2 hidden lg:table-cell">Invite Sent</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedEvents.map((ev) => (
                              <tr key={ev.id}
                                onClick={() => setSelectedEventId(ev.id === selectedEventId ? null : ev.id)}
                                className={`cursor-pointer transition-colors ${selectedEventId === ev.id ? 'bg-brand-500/10' : 'hover:vscode-bg-hover'}`}>
                                <td className="px-3 py-2.5 text-sm font-semibold vscode-text-primary">R{ev.round}</td>
                                <td className="px-3 py-2.5 text-sm vscode-text-primary whitespace-nowrap">
                                  {ev.scheduled_at ? fmt(ev.scheduled_at) : <span className="vscode-text-tertiary text-xs">TBD</span>}
                                </td>
                                <td className="px-3 py-2.5"><RoundStatusBadge status={ev.status} /></td>
                                <td className="px-3 py-2.5 text-sm vscode-text-secondary hidden sm:table-cell">
                                  {ev.duration_minutes != null ? `${ev.duration_minutes} min` : '—'}
                                </td>
                                <td className="px-3 py-2.5 text-sm vscode-text-secondary hidden sm:table-cell">
                                  {ev.interviewer_name ?? '—'}
                                </td>
                                <td className="px-3 py-2.5 text-xs vscode-text-tertiary hidden md:table-cell truncate max-w-[160px]">
                                  {ev.location ?? '—'}
                                </td>
                                <td className="px-3 py-2.5 text-xs vscode-text-tertiary hidden lg:table-cell whitespace-nowrap">
                                  {ev.invite_sent_at ? fmtDate(ev.invite_sent_at) : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </AttrGroup>
                  )}

                  {/* Expanded details for selected event */}
                  {selectedEventId && (() => {
                    const ev = sortedEvents.find(e => e.id === selectedEventId);
                    if (!ev) return null;
                    return (
                      <AttrGroup title={`Round ${ev.round} — Details`}>
                        <dl className="ggj-attr-grid text-sm">
                          <AttrRow label="Status"><RoundStatusBadge status={ev.status} /></AttrRow>
                          <AttrRow label="Scheduled at">{fmt(ev.scheduled_at)}</AttrRow>
                          <AttrRow label="Duration">{ev.duration_minutes != null ? `${ev.duration_minutes} min` : '—'}</AttrRow>
                          <AttrRow label="Location / link">{ev.location ?? '—'}</AttrRow>
                          <AttrRow label="Lead interviewer">{ev.interviewer_name ?? '—'}</AttrRow>
                          <AttrRow label="Lead email">{ev.interviewer_email ?? '—'}</AttrRow>
                          <AttrRow label="Invite sent">{fmt(ev.invite_sent_at)}</AttrRow>
                          <AttrRow label="Scheduling tool">
                            {ev.scheduling_provider
                              ? ev.scheduling_provider.charAt(0).toUpperCase() + ev.scheduling_provider.slice(1)
                              : '—'}
                          </AttrRow>
                          <AttrRow label="Booking URL">
                            {ev.scheduling_booking_url
                              ? <a href={ev.scheduling_booking_url} target="_blank" rel="noreferrer"
                                  className="text-brand-400 hover:underline inline-flex items-center gap-1">
                                  <Link2 className="w-3 h-3" /> Open link
                                </a>
                              : '—'}
                          </AttrRow>
                          <AttrRow label="Panelists">
                            {ev.attendees?.length
                              ? ev.attendees.map(a => a.name ?? a.email).filter(Boolean).join(', ')
                              : '—'}
                          </AttrRow>
                          {ev.scheduling_meta?.candidateAvailability && (
                            <AttrRow label="Candidate availability">{ev.scheduling_meta.candidateAvailability}</AttrRow>
                          )}
                          {ev.scheduling_meta?.panelAvailability && (
                            <AttrRow label="Panel availability">{ev.scheduling_meta.panelAvailability}</AttrRow>
                          )}
                          <AttrRow label="Notes">{ev.notes ?? '—'}</AttrRow>
                          <AttrRow label="Created">{fmtDate(ev.created_at)}</AttrRow>
                          <AttrRow label="Last updated">{fmtDate(ev.updated_at)}</AttrRow>
                        </dl>
                      </AttrGroup>
                    );
                  })()}

                  {/* Schedule Calendar */}
                  <AttrGroup title="Schedule Calendar">
                    <p className="text-xs vscode-text-tertiary mb-3">
                      Click a day to open the interview wizard pre-filled with that date (defaults to 9:00 local). Dots indicate scheduled rounds.
                    </p>
                    <InterviewMonthCalendar
                      monthAnchor={monthAnchor}
                      onMonthAnchorChange={setMonthAnchor}
                      events={monthEvents}
                      selectedId={selectedEventId}
                      onSelectEvent={(evId) => setSelectedEventId(evId)}
                      onDayClick={(day) => openWizardForDay(day)}
                    />
                  </AttrGroup>
                </div>
              )}

              {/* ═══════ PARAMETERS TAB ═══════ */}
              {activeTab === 'parameters' && (
                <div className="space-y-4">

                  {/* Object — name, description, type (matches candidate Parameters) */}
                  <div className="ggj-param-group overflow-hidden">
                    <div className="ggj-param-section-block border-b border-[color-mix(in_srgb,var(--vscode-border-primary)_55%,transparent)]">
                      <div className="ggj-profile-section-h">Object</div>
                      <div className="ggj-profile-section-body">
                        <dl className="ggj-attr-grid text-sm">
                          <dt className={PARAM_DT}>Name</dt>
                          <dd className={PARAM_DD}><span className="font-medium">{[cand?.full_name, detailApp?.job?.title].filter(Boolean).join(' — ') || '—'}</span></dd>
                          <dt className={PARAM_DT}>Object type</dt>
                          <dd className={`${PARAM_DD} vscode-text-secondary`}>Interview workspace (read-only)</dd>
                          <dt className={PARAM_DT}>Description</dt>
                          <dd className={`${PARAM_DD} min-w-0 lg:col-span-3`}>
                            <textarea
                              readOnly
                              tabIndex={-1}
                              rows={5}
                              value={cand?.notes?.trim() ? cand.notes : ''}
                              placeholder="—"
                              aria-label="Description"
                              className="ggj-param-readonly-textarea"
                            />
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>

                  {/* ── Candidate Parameters ── */}
                  <div className="ggj-param-group">
                    <div className="ggj-profile-section-h">Candidate Parameters</div>
                    <div className="ggj-profile-section-body">
                      <dl className="ggj-attr-grid text-sm">
                        <dt className={PARAM_DT}>Candidate ID</dt>       <dd className={PARAM_DD}><code className="text-xs break-all">{cand?.id ?? '—'}</code></dd>
                        <dt className={PARAM_DT}>Full name</dt>          <dd className={PARAM_DD}>{cand?.full_name ?? '—'}</dd>
                        <dt className={PARAM_DT}>Email</dt>              <dd className={PARAM_DD}>{cand?.email ? <a href={`mailto:${cand.email}`} className="text-brand-400 hover:underline">{cand.email}</a> : '—'}</dd>
                        <dt className={PARAM_DT}>Phone</dt>              <dd className={PARAM_DD}>{cand?.phone ?? '—'}</dd>
                        <dt className={PARAM_DT}>Location</dt>           <dd className={PARAM_DD}>{cand?.location ?? '—'}</dd>
                        <dt className={PARAM_DT}>Current title</dt>      <dd className={PARAM_DD}>{cand?.current_title ?? '—'}</dd>
                        <dt className={PARAM_DT}>Current company</dt>    <dd className={PARAM_DD}>{cand?.current_company ?? '—'}</dd>
                        <dt className={PARAM_DT}>Years experience</dt>   <dd className={PARAM_DD}>{cand?.years_experience != null ? `${cand.years_experience} yrs` : '—'}</dd>
                        <dt className={PARAM_DT}>Notice period</dt>      <dd className={PARAM_DD}>{cand?.notice_period_days != null ? `${cand.notice_period_days} days` : '—'}</dd>
                        <dt className={PARAM_DT}>Expected salary</dt>    <dd className={PARAM_DD}>{cand?.expected_salary ?? '—'}</dd>
                        <dt className={PARAM_DT}>Source</dt>             <dd className={PARAM_DD}>{cand?.source ?? '—'}</dd>
                        <dt className={PARAM_DT}>LinkedIn</dt>           <dd className={PARAM_DD}>{cand?.linkedin_url ? <a href={cand.linkedin_url} target="_blank" rel="noreferrer" className="text-brand-400 hover:underline inline-flex items-center gap-1">View <ExternalLink className="w-3 h-3" /></a> : '—'}</dd>
                        <dt className={PARAM_DT}>Resume</dt>             <dd className={PARAM_DD}>{cand?.resume_url ? <a href={cand.resume_url} target="_blank" rel="noreferrer" className="text-brand-400 hover:underline inline-flex items-center gap-1">Download <ExternalLink className="w-3 h-3" /></a> : '—'}</dd>
                        <dt className={PARAM_DT}>Created</dt>            <dd className={PARAM_DD}>{fmtDateLong(cand?.created_at)}</dd>
                        <dt className={PARAM_DT}>Updated</dt>            <dd className={PARAM_DD}>{fmtDateLong(cand?.updated_at)}</dd>
                      </dl>
                      {cand?.parsed_skills && cand.parsed_skills.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {cand.parsed_skills.map((s) => (
                            <span key={s} className="px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ background: `${BRAND}18`, color: BRAND, border: `1px solid ${BRAND}33` }}>{s}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ── Application Parameters (active role) ── */}
                  <div className="ggj-param-group">
                    <div className="ggj-profile-section-h">Application Parameters — {detailApp!.job?.title ?? 'Active Role'}</div>
                    <div className="ggj-profile-section-body">
                      <dl className="ggj-attr-grid text-sm">
                        <dt className={PARAM_DT}>Application ID</dt>     <dd className={PARAM_DD}><code className="text-xs break-all">{detailApp!.id}</code></dd>
                        <dt className={PARAM_DT}>Reference</dt>          <dd className={PARAM_DD}>{refLabel(detailApp!.id)}</dd>
                        <dt className={PARAM_DT}>Pipeline stage</dt>     <dd className={PARAM_DD}>{PIPELINE_STATUS_LABEL[detailApp!.status]}</dd>
                        <dt className={PARAM_DT}>Source</dt>             <dd className={PARAM_DD}>{detailApp!.source ?? '—'}</dd>
                        <dt className={PARAM_DT}>Fitment score</dt>      <dd className={PARAM_DD}>{detailApp!.fitment_score ?? '—'}</dd>
                        <dt className={PARAM_DT}>Assigned to</dt>        <dd className={PARAM_DD}>{detailApp!.assigned_user?.display_name ?? detailApp!.assigned_user?.email ?? '—'}</dd>
                        <dt className={PARAM_DT}>Submitted</dt>          <dd className={PARAM_DD}>{fmtDateLong(detailApp!.submitted_at)}</dd>
                        <dt className={PARAM_DT}>Last updated</dt>       <dd className={PARAM_DD}>{fmtDateLong(detailApp!.updated_at)}</dd>
                        <dt className={PARAM_DT}>Total roles</dt>        <dd className={PARAM_DD}>{apps.length}</dd>
                        <dt className={PARAM_DT}>Total rounds</dt>       <dd className={PARAM_DD}>{eventsForSelected.length || '—'}</dd>
                        <dt className={PARAM_DT}>Rounds conducted</dt>   <dd className={PARAM_DD}>{eventsForSelected.filter(e => e.status === 'conducted').length || '—'}</dd>
                        <dt className={PARAM_DT}>Rounds scheduled</dt>   <dd className={PARAM_DD}>{eventsForSelected.filter(e => e.status === 'scheduled').length || '—'}</dd>
                      </dl>
                    </div>
                  </div>

                  {/* ── Interview Round Parameters ── */}
                  {sortedEvents.length > 0 && (
                    <div className="ggj-param-group">
                      <div className="ggj-profile-section-h">Interview Round Parameters</div>
                      <div className="ggj-profile-section-body overflow-x-auto">
                        <table className="ggj-data-table w-full text-sm">
                          <thead>
                            <tr className="border-b vscode-border">
                              <th className="px-3 py-2 text-left text-xs">Round</th>
                              <th className="px-3 py-2 text-left text-xs">Status</th>
                              <th className="px-3 py-2 text-left text-xs">Scheduled At</th>
                              <th className="px-3 py-2 text-left text-xs">Duration</th>
                              <th className="px-3 py-2 text-left text-xs">Lead Interviewer</th>
                              <th className="px-3 py-2 text-left text-xs">Lead Email</th>
                              <th className="px-3 py-2 text-left text-xs">Location</th>
                              <th className="px-3 py-2 text-left text-xs">Invite Sent</th>
                              <th className="px-3 py-2 text-left text-xs">Created</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedEvents.map((ev) => (
                              <tr key={ev.id} className="hover:vscode-bg-hover">
                                <td className="px-3 py-2 font-semibold">R{ev.round}</td>
                                <td className="px-3 py-2"><RoundStatusBadge status={ev.status} /></td>
                                <td className="px-3 py-2 whitespace-nowrap text-xs vscode-text-secondary">{fmt(ev.scheduled_at)}</td>
                                <td className="px-3 py-2 text-xs vscode-text-secondary">{ev.duration_minutes != null ? `${ev.duration_minutes} min` : '—'}</td>
                                <td className="px-3 py-2 text-xs vscode-text-secondary">{ev.interviewer_name ?? '—'}</td>
                                <td className="px-3 py-2 text-xs vscode-text-secondary">{ev.interviewer_email ?? '—'}</td>
                                <td className="px-3 py-2 text-xs vscode-text-secondary truncate max-w-[140px]">{ev.location ?? '—'}</td>
                                <td className="px-3 py-2 text-xs vscode-text-secondary whitespace-nowrap">{ev.invite_sent_at ? fmtDate(ev.invite_sent_at) : '—'}</td>
                                <td className="px-3 py-2 text-xs vscode-text-secondary whitespace-nowrap">{fmtDate(ev.created_at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* ── Job Parameters ── */}
                  <div className="ggj-param-group">
                    <div className="ggj-profile-section-h">Job Parameters</div>
                    <div className="ggj-profile-section-body">
                      <dl className="ggj-attr-grid text-sm">
                        <dt className={PARAM_DT}>Job ID</dt>            <dd className={PARAM_DD}><code className="text-xs break-all">{job?.id ?? '—'}</code></dd>
                        <dt className={PARAM_DT}>Title</dt>             <dd className={PARAM_DD}>{job?.id ? <Link to={`/jobs/${job.id}`} className="text-brand-400 hover:underline">{job.title}</Link> : '—'}</dd>
                        <dt className={PARAM_DT}>Status</dt>            <dd className={PARAM_DD}><span className="capitalize">{job?.status ?? '—'}</span></dd>
                        <dt className={PARAM_DT}>Employment type</dt>   <dd className={PARAM_DD}>{job?.employment_type ?? '—'}</dd>
                        <dt className={PARAM_DT}>Department</dt>        <dd className={PARAM_DD}>{job?.department ?? '—'}</dd>
                        <dt className={PARAM_DT}>Location</dt>          <dd className={PARAM_DD}>{job?.location ?? '—'}</dd>
                        <dt className={PARAM_DT}>Min experience</dt>    <dd className={PARAM_DD}>{job?.min_experience_years != null ? `${job.min_experience_years} yrs` : '—'}</dd>
                        <dt className={PARAM_DT}>Salary range</dt>      <dd className={PARAM_DD}>{job?.salary_range ?? '—'}</dd>
                        <dt className={PARAM_DT}>JD URL</dt>            <dd className={PARAM_DD}>{job?.jd_url ? <a href={job.jd_url} target="_blank" rel="noreferrer" className="text-brand-400 hover:underline inline-flex items-center gap-1">View JD <ExternalLink className="w-3 h-3" /></a> : '—'}</dd>
                        <dt className={PARAM_DT}>Created</dt>           <dd className={PARAM_DD}>{fmtDateLong(job?.created_at)}</dd>
                      </dl>
                      {job?.skills_required && job.skills_required.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          <p className="w-full text-[10px] uppercase tracking-wide vscode-text-tertiary font-semibold mb-1">Required Skills</p>
                          {job.skills_required.map((s) => (
                            <span key={s} className="px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ background: `${BRAND}18`, color: BRAND, border: `1px solid ${BRAND}33` }}>{s}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ── Client Parameters ── */}
                  <div className="ggj-param-group">
                    <div className="ggj-profile-section-h">Client Parameters</div>
                    <div className="ggj-profile-section-body">
                      <dl className="ggj-attr-grid text-sm">
                        <dt className={PARAM_DT}>Client ID</dt>         <dd className={PARAM_DD}><code className="text-xs break-all">{client?.id ?? '—'}</code></dd>
                        <dt className={PARAM_DT}>Name</dt>              <dd className={PARAM_DD}>{client?.id ? <Link to={`/clients/${client.id}`} className="text-brand-400 hover:underline">{client.name}</Link> : '—'}</dd>
                        <dt className={PARAM_DT}>Industry</dt>          <dd className={PARAM_DD}>{client?.industry ?? '—'}</dd>
                        <dt className={PARAM_DT}>Location</dt>          <dd className={PARAM_DD}>{client?.location ?? '—'}</dd>
                        <dt className={PARAM_DT}>Contact name</dt>      <dd className={PARAM_DD}>{client?.contact_name ?? '—'}</dd>
                        <dt className={PARAM_DT}>Contact email</dt>     <dd className={PARAM_DD}>{client?.contact_email ? <a href={`mailto:${client.contact_email}`} className="text-brand-400 hover:underline">{client.contact_email}</a> : '—'}</dd>
                        <dt className={PARAM_DT}>Contact phone</dt>     <dd className={PARAM_DD}>{client?.contact_phone ?? '—'}</dd>
                        <dt className={PARAM_DT}>Website</dt>           <dd className={PARAM_DD}>{client?.website ? <a href={client.website} target="_blank" rel="noreferrer" className="text-brand-400 hover:underline inline-flex items-center gap-1">Visit <ExternalLink className="w-3 h-3" /></a> : '—'}</dd>
                        <dt className={PARAM_DT}>Client since</dt>      <dd className={PARAM_DD}>{fmtDateLong(client?.created_at)}</dd>
                      </dl>
                    </div>
                  </div>

                  {/* ── System / Audit Parameters ── */}
                  <div className="ggj-param-group">
                    <div className="ggj-profile-section-h">System / Audit</div>
                    <div className="ggj-profile-section-body">
                      <dl className="ggj-attr-grid text-sm">
                        <dt className={PARAM_DT}>Application ID</dt>    <dd className={PARAM_DD}><code className="text-xs break-all">{detailApp!.id}</code></dd>
                        <dt className={PARAM_DT}>Candidate ID</dt>      <dd className={PARAM_DD}><code className="text-xs break-all">{cand?.id ?? '—'}</code></dd>
                        <dt className={PARAM_DT}>Job ID</dt>            <dd className={PARAM_DD}><code className="text-xs break-all">{job?.id ?? '—'}</code></dd>
                        <dt className={PARAM_DT}>Client ID</dt>         <dd className={PARAM_DD}><code className="text-xs break-all">{client?.id ?? '—'}</code></dd>
                        <dt className={PARAM_DT}>Org ID</dt>            <dd className={PARAM_DD}><code className="text-xs break-all">{user?.orgId ?? '—'}</code></dd>
                        <dt className={PARAM_DT}>Assigned user ID</dt>  <dd className={PARAM_DD}><code className="text-xs break-all">{detailApp!.assigned_to ?? '—'}</code></dd>
                        <dt className={PARAM_DT}>Created</dt>           <dd className={PARAM_DD}>{fmtDateLong(detailApp!.created_at)}</dd>
                        <dt className={PARAM_DT}>Last updated</dt>      <dd className={PARAM_DD}>{fmtDateLong(detailApp!.updated_at)}</dd>
                      </dl>
                    </div>
                  </div>

                </div>
              )}

              {/* ═══════ EMAIL TAB ═══════ */}
              {activeTab === 'email' && (() => {
                const rootThreads = emailThreads.filter(t => !t.in_reply_to_thread_id);
                const repliesMap: Record<string, EmailThread[]> = {};
                emailThreads.filter(t => t.in_reply_to_thread_id).forEach(t => {
                  if (!repliesMap[t.in_reply_to_thread_id!]) repliesMap[t.in_reply_to_thread_id!] = [];
                  repliesMap[t.in_reply_to_thread_id!].push(t);
                });
                const displayList = rootThreads.length > 0 ? rootThreads : emailThreads;

                return (
                  <div className="space-y-4">

                    <AttrGroup title="Email Threads">
                      {emailThreads.length === 0 ? (
                        <div className="py-6 text-center space-y-3">
                          <MailOpen className="w-9 h-9 vscode-text-tertiary mx-auto" />
                          <p className="vscode-text-secondary text-sm">No emails for this application yet.</p>
                          <Link to={`/emails/compose?applicationId=${detailApp!.id}`}
                            className="btn-primary text-sm inline-flex items-center gap-1.5">
                            <Mail className="w-4 h-4" /> Compose First Email
                          </Link>
                        </div>
                      ) : (
                        <dl className="ggj-attr-grid text-sm">
                          <dt className="py-2 text-xs vscode-text-tertiary font-medium leading-5 pr-2">Threads</dt>
                          <dd className="py-2 text-sm vscode-text-primary leading-5">
                            <div className="space-y-1.5">
                              {displayList.map((thread) => {
                                const replies = repliesMap[thread.id] ?? [];
                                const isResponded = !!thread.cta_response;
                                const isAwaiting  = !isResponded && !!thread.sent_at;
                                return (
                                  <button key={thread.id} type="button"
                                    onClick={() => { setEmailDetailThread(thread); setEmailAccordionOpen({ body: true, cta: false, track: false, replies: replies.length > 0 }); }}
                                    className="flex items-center gap-2 text-brand-400 hover:text-brand-300 hover:underline text-sm text-left group w-full">
                                    <span className="truncate">{thread.subject || '(No subject)'}</span>
                                    {isAwaiting && <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0 animate-pulse" />}
                                    {isResponded && <CheckCircle className="w-3.5 h-3.5 text-green-400 shrink-0" />}
                                    {replies.length > 0 && <span className="text-[10px] vscode-text-tertiary shrink-0">(+{replies.length})</span>}
                                  </button>
                                );
                              })}
                            </div>
                          </dd>

                          <dt className="py-2 text-xs vscode-text-tertiary border-t vscode-border font-medium leading-5 pr-2">Candidate</dt>
                          <dd className="py-2 text-sm border-t vscode-border">
                            {cand?.email ? <a href={`mailto:${cand.email}`} className="text-brand-400 hover:underline">{cand.email}</a> : '—'}
                          </dd>

                          <dt className="py-2 text-xs vscode-text-tertiary border-t vscode-border font-medium leading-5 pr-2">Client contact</dt>
                          <dd className="py-2 text-sm border-t vscode-border">
                            {client?.contact_email ? <a href={`mailto:${client.contact_email}`} className="text-brand-400 hover:underline">{client.contact_email}</a> : '—'}
                          </dd>

                          <dt className="py-2 text-xs vscode-text-tertiary border-t vscode-border font-medium leading-5 pr-2">Sent</dt>
                          <dd className="py-2 text-sm vscode-text-primary border-t vscode-border">{emailThreads.length}</dd>

                          <dt className="py-2 text-xs vscode-text-tertiary border-t vscode-border font-medium leading-5 pr-2">Responded</dt>
                          <dd className="py-2 text-sm vscode-text-primary font-medium border-t vscode-border">{emailThreads.filter(t => t.cta_response).length}</dd>

                          <dt className="py-2 text-xs vscode-text-tertiary border-t vscode-border font-medium leading-5 pr-2">Awaiting reply</dt>
                          <dd className="py-2 text-sm font-medium border-t vscode-border" style={{ color: BRAND }}>{emailThreads.filter(t => !t.cta_response && t.sent_at).length}</dd>

                          <dt className="py-2 text-xs vscode-text-tertiary border-t vscode-border font-medium leading-5 pr-2">Total opens</dt>
                          <dd className="py-2 text-sm vscode-text-primary border-t vscode-border">{emailThreads.reduce((s, t) => s + (t.open_count || 0), 0)}</dd>

                          <dt className="py-2 text-xs vscode-text-tertiary border-t vscode-border font-medium leading-5 pr-2">Last sent</dt>
                          <dd className="py-2 text-sm vscode-text-primary border-t vscode-border">
                            {emailThreads.length > 0
                              ? formatDateTimeGmt(new Date(Math.max(...emailThreads.map(t => new Date(t.sent_at ?? 0).getTime()))).toISOString())
                              : '—'}
                          </dd>

                          <dt className="py-2 text-xs vscode-text-tertiary border-t vscode-border font-medium leading-5 pr-2">Assigned to</dt>
                          <dd className="py-2 text-sm vscode-text-primary border-t vscode-border">{detailApp!.assigned_user?.display_name ?? detailApp!.assigned_user?.email ?? '—'}</dd>
                        </dl>
                      )}
                    </AttrGroup>

                    {emailThreads.length > 0 && (
                      <AttrGroup title="Email Engagement Summary">
                        <dl className="ggj-attr-grid text-sm">
                          <AttrRow label="Total threads">{emailThreads.length}</AttrRow>
                          <AttrRow label="Responded"><span className="vscode-text-primary font-medium">{emailThreads.filter(t => t.cta_response).length}</span></AttrRow>
                          <AttrRow label="Awaiting reply"><span className="font-medium" style={{ color: BRAND }}>{emailThreads.filter(t => !t.cta_response && t.sent_at).length}</span></AttrRow>
                          <AttrRow label="Total opens">{emailThreads.reduce((s, t) => s + (t.open_count || 0), 0)}</AttrRow>
                          <AttrRow label="With replies">{emailThreads.filter(t => t.in_reply_to_thread_id).length}</AttrRow>
                          <AttrRow label="Response rate">
                            {emailThreads.length > 0
                              ? `${Math.round((emailThreads.filter(t => t.cta_response).length / emailThreads.length) * 100)}%`
                              : '—'}
                          </AttrRow>
                          <AttrRow label="Candidate email">
                            {cand?.email ? <a href={`mailto:${cand.email}`} className="text-brand-400 hover:underline">{cand.email}</a> : '—'}
                          </AttrRow>
                          <AttrRow label="Client contact">
                            {client?.contact_email ? <a href={`mailto:${client.contact_email}`} className="text-brand-400 hover:underline">{client.contact_email}</a> : '—'}
                          </AttrRow>
                          <AttrRow label="First sent">
                            {emailThreads.length > 0
                              ? formatDateTimeGmt(new Date(Math.min(...emailThreads.map(t => new Date(t.sent_at ?? Date.now()).getTime()))).toISOString())
                              : '—'}
                          </AttrRow>
                          <AttrRow label="Last sent">
                            {emailThreads.length > 0
                              ? formatDateTimeGmt(new Date(Math.max(...emailThreads.map(t => new Date(t.sent_at ?? 0).getTime()))).toISOString())
                              : '—'}
                          </AttrRow>
                        </dl>
                      </AttrGroup>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </div>

        {/* ── RIGHT: sidebar panels — hidden on Parameters tab ── */}
        {activeTab !== 'parameters' && <aside className="ggj-rail-aside space-y-4 lg:sticky lg:top-4 lg:self-start">

          {/* Quick links */}
          <CandidateCollapsiblePanel storageKey={`iview-${candidateId}-${activeTab}-links`} title="Quick Links" defaultOpen>
            <div className="space-y-0.5 px-1">
              {contextQuickLinks.map((l) => (
                <Link key={l.key} to={l.to}
                  className="flex items-center justify-between gap-2 py-1.5 text-sm text-brand-400 hover:text-brand-300">
                  <span className="truncate">{l.label}</span>
                  <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                </Link>
              ))}
            </div>
          </CandidateCollapsiblePanel>

          {/* Scheduling Pulse */}
          {appReady && (
            <CandidateCollapsiblePanel storageKey={`iview-${candidateId}-sched-pulse`} title="Scheduling Pulse" defaultOpen>
              <div className="space-y-3 px-1">
                <div>
                  <p className="text-[10px] uppercase tracking-wide vscode-text-tertiary mb-1.5 font-semibold">Pipeline</p>
                  <StatusBadge status={detailApp!.status} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide vscode-text-tertiary mb-1 font-semibold">Next Scheduled</p>
                  <p className="text-sm vscode-text-primary font-medium flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5 opacity-60" />
                    {fmt(nextScheduled)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide vscode-text-tertiary mb-1 font-semibold">Rounds</p>
                  <p className="text-xs vscode-text-secondary">
                    {eventsForSelected.length} total · {eventsForSelected.filter(e => e.status === 'conducted').length} done ·{' '}
                    {eventsForSelected.filter(e => e.status === 'scheduled').length} upcoming
                  </p>
                </div>
                {primaryEvent ? (
                  <div>
                    <p className="text-[10px] uppercase tracking-wide vscode-text-tertiary mb-2 font-semibold">
                      Latest · R{primaryEvent.round}
                    </p>
                    <div className="flex items-center justify-between gap-0.5">
                      {LIFECYCLE_LABELS.map((label, i) => {
                        const terminal = phaseIdx === 4;
                        const done    = terminal ? i < 4 : i < phaseIdx;
                        const current = !terminal && i === phaseIdx;
                        const closed  = terminal && i === 4;
                        return (
                          <div key={label} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                            <div
                              className={`w-2.5 h-2.5 rounded-full shrink-0 transition-all ${
                                closed && primaryEvent.status === 'conducted' ? 'bg-emerald-500' :
                                closed && primaryEvent.status === 'no_show'   ? 'bg-red-500' :
                                closed   ? 'bg-gray-500/50' :
                                done     ? 'bg-brand-500' :
                                current  ? 'animate-pulse ring-2 ring-brand-400 ring-offset-1 bg-brand-500/60' :
                                           'vscode-bg-tertiary border border-[var(--vscode-border-primary)]'
                              }`}
                              title={label}
                            />
                            <span className="text-[8px] text-center vscode-text-tertiary leading-tight truncate w-full">{label}</span>
                          </div>
                        );
                      })}
                    </div>
                    {primaryEvent.interviewer_name && (
                      <p className="text-[10px] vscode-text-tertiary mt-2 flex items-center gap-1">
                        <User className="w-3 h-3" /> Lead: {primaryEvent.interviewer_name}
                      </p>
                    )}
                    {primaryEvent.location && (
                      <p className="text-[10px] vscode-text-tertiary mt-0.5 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {primaryEvent.location}
                      </p>
                    )}
                    {primaryEvent.duration_minutes && (
                      <p className="text-[10px] vscode-text-tertiary mt-0.5 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {primaryEvent.duration_minutes} min
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs vscode-text-tertiary">No interview rounds yet.</p>
                )}
              </div>
            </CandidateCollapsiblePanel>
          )}

          {/* Application timeline (overview + email) */}
          {appReady && (activeTab === 'overview' || activeTab === 'email') && (
            <ApplicationHeartbeatTimeline
              applicationId={detailApp!.id}
              timeline={timeline}
              nextActionIndex={nextActionIndex}
            />
          )}

          {/* Email engagement stats (email tab only) */}
          {activeTab === 'email' && (
            <CandidateCollapsiblePanel storageKey={`iview-${candidateId}-em-stats`} title="Engagement" defaultOpen>
              <div className="space-y-1.5 px-1 text-xs">
                <div className="flex justify-between"><span className="vscode-text-tertiary">Sent</span><span className="vscode-text-primary font-medium">{emailThreads.length}</span></div>
                <div className="flex justify-between"><span className="vscode-text-tertiary">Opened</span><span className="vscode-text-primary font-medium">{emailThreads.filter(t => t.open_count > 0).length}</span></div>
                <div className="flex justify-between"><span className="vscode-text-tertiary">Responded</span><span className="vscode-text-primary font-medium">{emailThreads.filter(t => t.cta_response).length}</span></div>
                <div className="flex justify-between"><span className="vscode-text-tertiary">Awaiting</span><span className="font-medium" style={{ color: BRAND }}>{emailThreads.filter(t => !t.cta_response && t.sent_at).length}</span></div>
                <div className="flex justify-between"><span className="vscode-text-tertiary">Total opens</span><span className="vscode-text-primary font-medium">{emailThreads.reduce((s, t) => s + (t.open_count || 0), 0)}</span></div>
              </div>
            </CandidateCollapsiblePanel>
          )}

          {/* Candidate snapshot (hiring tab) */}
          {activeTab === 'hiring' && cand && (
            <CandidateCollapsiblePanel storageKey={`iview-${candidateId}-cand-snap`} title="Candidate Snapshot" defaultOpen>
              <div className="space-y-2 px-1">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0"
                    style={{ background: `linear-gradient(135deg, ${BRAND}, #6c63ff)` }}>
                    {(cand.full_name ?? '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold vscode-text-primary truncate">{cand.full_name}</p>
                    <p className="text-xs vscode-text-tertiary truncate">{cand.current_title ?? '—'}</p>
                  </div>
                </div>
                <p className="text-xs vscode-text-secondary">{cand.email ?? '—'}</p>
                <p className="text-xs vscode-text-secondary flex items-center gap-1">
                  <MapPin className="w-3 h-3 opacity-60" />{cand.location ?? '—'}
                </p>
                {cand.id && (
                  <Link to={`/candidates/${cand.id}`}
                    className="text-xs text-brand-400 hover:underline inline-flex items-center gap-1 pt-1">
                    View full profile <ExternalLink className="w-3 h-3" />
                  </Link>
                )}
              </div>
            </CandidateCollapsiblePanel>
          )}
        </aside>}
      </div>

      {/* ── Application detail modal (Candidate tab) ── */}
      {appModal && (
        <CandidateApplicationDetailModal
          app={appModal}
          teamMembers={[]}
          onClose={() => setAppModal(null)}
        />
      )}

      {/* ── Email thread detail modal ── */}
      {emailDetailThread && (() => {
        const th = emailDetailThread;
        const replies = emailThreads
          .filter(t => t.in_reply_to_thread_id === th.id)
          .sort((a, b) => new Date(a.sent_at ?? a.created_at).getTime() - new Date(b.sent_at ?? b.created_at).getTime());

        type StepStatus = 'done' | 'active' | 'pending' | 'awaiting';
        interface StepItem {
          key: string;
          label: string;
          sublabel?: string;
          timestamp?: string | null;
          status: StepStatus;
          icon: React.ReactNode;
          content?: React.ReactNode;
        }
        const steps: StepItem[] = [];

        steps.push({
          key: 'sent',
          label: 'Email Sent',
          sublabel: `To: ${th.recipient_name || th.recipient_email}`,
          timestamp: th.sent_at,
          status: th.sent_at ? 'done' : 'pending',
          icon: <Send className="w-3.5 h-3.5" />,
          content: (
            <div className="space-y-2">
              <div className="rounded-xl border vscode-border overflow-hidden">
                <div className="px-4 py-2 vscode-bg-tertiary border-b vscode-border flex items-center gap-2">
                  <span className="text-xs font-medium vscode-text-secondary">Subject:</span>
                  <span className="text-xs vscode-text-primary font-medium">{th.subject || '(No subject)'}</span>
                </div>
                <div className="px-4 py-3 max-h-56 overflow-y-auto">
                  {th.body_html
                    ? <div className="text-sm vscode-text-primary" dangerouslySetInnerHTML={{ __html: th.body_html }} />
                    : <p className="text-sm vscode-text-tertiary italic">No body content.</p>}
                </div>
              </div>
            </div>
          ),
        });

        if (th.opened_at || th.open_count > 0) {
          steps.push({
            key: 'opened',
            label: 'Email Opened',
            sublabel: th.open_count > 1 ? `${th.open_count}× opened` : 'Opened',
            timestamp: th.opened_at,
            status: 'done',
            icon: <MailOpen className="w-3.5 h-3.5" />,
            content: (
              <dl className="ggj-attr-grid text-sm">
                <AttrRow label="First opened">{th.opened_at ? formatTimelineDate(th.opened_at) : '—'}</AttrRow>
                <AttrRow label="Total opens">{th.open_count}</AttrRow>
              </dl>
            ),
          });
        }

        if (th.followup_count > 0) {
          steps.push({
            key: 'followup',
            label: `Follow-up${th.followup_count > 1 ? 's' : ''} Sent`,
            sublabel: `${th.followup_count} follow-up${th.followup_count > 1 ? 's' : ''}`,
            timestamp: th.next_followup_at,
            status: 'done',
            icon: <Reply className="w-3.5 h-3.5" />,
            content: (
              <dl className="ggj-attr-grid text-sm">
                <AttrRow label="Follow-ups sent">{th.followup_count}</AttrRow>
                <AttrRow label="Next follow-up">{th.next_followup_at ? formatTimelineDate(th.next_followup_at) : th.followup_stopped ? 'Stopped' : '—'}</AttrRow>
                <AttrRow label="Auto follow-up">{th.followup_stopped ? 'Stopped' : 'Active'}</AttrRow>
              </dl>
            ),
          });
        }

        replies.forEach((r, i) => {
          steps.push({
            key: `reply-${r.id}`,
            label: `Reply ${i + 1}`,
            sublabel: r.recipient_name || r.recipient_email,
            timestamp: r.sent_at,
            status: 'done',
            icon: <MessageSquare className="w-3.5 h-3.5" />,
            content: (
              <div className="rounded-xl border vscode-border overflow-hidden">
                <div className="px-4 py-2 vscode-bg-tertiary border-b vscode-border">
                  <span className="text-xs vscode-text-primary">{r.subject || '(No subject)'}</span>
                </div>
                <div className="px-4 py-3 max-h-48 overflow-y-auto">
                  {r.body_html
                    ? <div className="text-sm vscode-text-primary" dangerouslySetInnerHTML={{ __html: r.body_html }} />
                    : <p className="text-sm vscode-text-tertiary italic">No body.</p>}
                </div>
              </div>
            ),
          });
        });

        if (th.cta_response) {
          steps.push({
            key: 'cta',
            label: `Client Responded — ${CTA_LABEL[th.cta_response]}`,
            sublabel: th.cta_response_note ?? undefined,
            timestamp: th.cta_responded_at,
            status: 'done',
            icon: th.cta_response === 'accept' ? <CheckCircle className="w-3.5 h-3.5" /> : th.cta_response === 'reject' ? <XCircle className="w-3.5 h-3.5" /> : <HelpCircle className="w-3.5 h-3.5" />,
            content: (
              <dl className="ggj-attr-grid text-sm">
                <AttrRow label="Response">{CTA_LABEL[th.cta_response]}</AttrRow>
                <AttrRow label="Responded at">{th.cta_responded_at ? formatTimelineDate(th.cta_responded_at) : '—'}</AttrRow>
                {th.cta_response_note && <AttrRow label="Note">{th.cta_response_note}</AttrRow>}
              </dl>
            ),
          });
        } else if (th.sent_at) {
          steps.push({
            key: 'awaiting',
            label: 'Awaiting Reply',
            sublabel: 'No response yet',
            timestamp: null,
            status: 'awaiting',
            icon: <Clock className="w-3.5 h-3.5" />,
            content: (
              <div className="space-y-2">
                <p className="text-xs vscode-text-secondary">The client has not responded to the CTA link yet.</p>
                <Link to={`/emails/compose?applicationId=${selectedApplicationId}&replyTo=${th.id}`}
                  className="text-xs text-brand-400 hover:underline inline-flex items-center gap-1">
                  <Reply className="w-3 h-3" /> Send follow-up
                </Link>
              </div>
            ),
          });
        }

        const TIPS = [
          { icon: <Eye className="w-3.5 h-3.5" />,          text: 'Track when the client opens your email via the open count.' },
          { icon: <Zap className="w-3.5 h-3.5" />,          text: 'CTA buttons let the client shortlist, pass, or query directly from the email.' },
          { icon: <Reply className="w-3.5 h-3.5" />,         text: 'Send a follow-up if no response after 48 hours to keep the process moving.' },
          { icon: <AlertCircle className="w-3.5 h-3.5" />,   text: 'Opened but no response usually means the client is reviewing — give it 1–2 days.' },
          { icon: <Inbox className="w-3.5 h-3.5" />,         text: 'Replies are linked back to this thread for a complete communication trail.' },
        ];

        return (
          <div className="fixed inset-0 z-[300] flex items-start justify-center p-4 bg-black/60 backdrop-blur-md overflow-y-auto"
            onClick={(e) => { if (e.target === e.currentTarget) setEmailDetailThread(null); }}>
            <div className="w-full max-w-4xl my-6 rounded-2xl overflow-hidden shadow-2xl border vscode-border flex flex-col"
              style={{ background: 'var(--vscode-bg-secondary, #1e1e2e)' }}>

              {/* Header */}
              <div className="flex items-start justify-between gap-3 px-6 py-4 shrink-0"
                style={{ background: 'linear-gradient(135deg, #8B80F9 0%, #6c63ff 100%)' }}>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold text-white/60 uppercase tracking-widest mb-1 flex items-center gap-1.5">
                    <Mail className="w-3 h-3" /> Email Communication
                  </p>
                  <h2 className="text-base font-bold text-white leading-tight">{th.subject || '(No subject)'}</h2>
                  <p className="text-xs text-white/65 mt-1">
                    To: {th.recipient_name || th.recipient_email}
                    {th.sent_at && ` · ${formatTimelineDate(th.sent_at)}`}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link to={`/emails/thread/${th.id}`} target="_blank"
                    className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Link>
                  <button type="button" onClick={() => setEmailDetailThread(null)}
                    className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Body: two-column — stepper left, tips right */}
              <div className="flex flex-col lg:flex-row flex-1 overflow-hidden min-h-0 max-h-[72vh]">

                {/* LEFT: Vertical stepper */}
                <div className="flex-1 overflow-y-auto px-6 py-5 min-w-0">
                  <p className="text-xs font-semibold vscode-text-tertiary uppercase tracking-wide mb-4">Communication Timeline</p>
                  <div className="space-y-0">
                    {steps.map((step, idx) => {
                      const isLast     = idx === steps.length - 1;
                      const isOpen     = emailAccordionOpen[step.key] ?? false;
                      const isDone     = step.status === 'done';
                      const isAwaiting = step.status === 'awaiting';
                      const circleStyle = isDone
                        ? { background: '#8B80F9', border: '2px solid #8B80F9', color: '#fff' }
                        : isAwaiting
                        ? { background: 'transparent', border: '2px solid #f59e0b', color: '#f59e0b' }
                        : { background: 'transparent', border: '2px solid var(--vscode-border-primary)', color: 'var(--vscode-text-tertiary)' };
                      return (
                        <div key={step.key} className="flex gap-4">
                          <div className="flex flex-col items-center" style={{ width: 28 }}>
                            <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 z-10 relative" style={circleStyle}>
                              {isDone
                                ? (idx === 0 ? step.icon : <CheckCircle className="w-3.5 h-3.5" />)
                                : isAwaiting
                                ? <span className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-pulse" />
                                : step.icon}
                            </div>
                            {!isLast && (
                              <div className="flex-1 w-px mt-1"
                                style={{ background: isDone ? '#8B80F9' : 'var(--vscode-border-primary)', minHeight: isOpen ? 16 : 32 }} />
                            )}
                          </div>
                          <div className={`flex-1 min-w-0 ${isLast ? '' : 'pb-4'}`}>
                            <button type="button"
                              className="w-full text-left flex items-start justify-between gap-2 group"
                              onClick={() => setEmailAccordionOpen(prev => ({ ...prev, [step.key]: !prev[step.key] }))}>
                              <div className="min-w-0">
                                <p className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${isDone ? '' : isAwaiting ? 'text-amber-400' : 'vscode-text-tertiary'}`}
                                  style={{ color: isDone ? '#8B80F9' : undefined }}>
                                  {isDone ? `Step ${idx + 1}` : isAwaiting ? 'Pending' : `Step ${idx + 1}`}
                                </p>
                                <p className={`text-sm font-semibold ${isDone ? 'vscode-text-primary' : isAwaiting ? 'text-amber-300' : 'vscode-text-tertiary'}`}>
                                  {step.label}
                                  {isAwaiting && <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-amber-400 align-middle" />}
                                </p>
                                {step.sublabel && <p className="text-xs vscode-text-tertiary mt-0.5">{step.sublabel}</p>}
                                {step.timestamp && (
                                  <p className="text-[10px] vscode-text-tertiary mt-0.5 flex items-center gap-1">
                                    <Clock className="w-2.5 h-2.5" />{formatTimelineDate(step.timestamp)}
                                  </p>
                                )}
                              </div>
                              {step.content && (
                                <ChevronDown className={`w-4 h-4 vscode-text-tertiary shrink-0 mt-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                              )}
                            </button>
                            {step.content && isOpen && (
                              <div className="mt-3 rounded-xl border vscode-border overflow-hidden"
                                style={{ background: 'var(--vscode-bg-tertiary, #2a2a3e)' }}>
                                <div className="p-4">{step.content}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* RIGHT: Quick tips */}
                <div className="w-full lg:w-[220px] shrink-0 border-t lg:border-t-0 lg:border-l vscode-border overflow-y-auto"
                  style={{ background: 'var(--vscode-bg-tertiary, #2a2a3e)' }}>
                  <div className="px-4 py-4 space-y-4">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#8B80F9' }}>Status</p>
                      {th.cta_response ? (
                        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full font-medium"
                          style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>
                          {CTA_ICON[th.cta_response]} {CTA_LABEL[th.cta_response]}
                        </span>
                      ) : th.sent_at ? (
                        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full font-medium"
                          style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" /> Awaiting reply
                        </span>
                      ) : (
                        <span className="text-xs vscode-text-tertiary">Draft</span>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#8B80F9' }}>Engagement</p>
                      <div className="space-y-1.5 text-xs">
                        <div className="flex justify-between"><span className="vscode-text-tertiary">Opens</span><span className="vscode-text-primary font-medium">{th.open_count || 0}</span></div>
                        <div className="flex justify-between"><span className="vscode-text-tertiary">Follow-ups</span><span className="vscode-text-primary font-medium">{th.followup_count || 0}</span></div>
                        <div className="flex justify-between"><span className="vscode-text-tertiary">Replies</span><span className="vscode-text-primary font-medium">{replies.length}</span></div>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#8B80F9' }}>Quick Tips</p>
                      <div className="space-y-2.5">
                        {TIPS.map((tip, i) => (
                          <div key={i} className="flex gap-2 text-[11px] vscode-text-tertiary leading-snug">
                            <span className="shrink-0 mt-0.5" style={{ color: '#8B80F9' }}>{tip.icon}</span>
                            <span>{tip.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between gap-3 px-6 py-3 border-t vscode-border shrink-0"
                style={{ background: 'var(--vscode-bg-secondary, #1e1e2e)' }}>
                <Link to={`/emails/compose?applicationId=${selectedApplicationId}&replyTo=${th.id}`}
                  className="btn-primary text-sm inline-flex items-center gap-1.5">
                  <Reply className="w-4 h-4" /> Reply
                </Link>
                <div className="flex items-center gap-2">
                  <Link to={`/emails/thread/${th.id}`} target="_blank"
                    className="btn-secondary text-sm inline-flex items-center gap-1.5">
                    <ExternalLink className="w-3.5 h-3.5" /> Full page
                  </Link>
                  <button type="button" onClick={() => setEmailDetailThread(null)} className="btn-secondary text-sm">Close</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Wizard */}
      {appReady && (
        <InterviewWizardModal
          open={wizardOpen}
          onClose={closeWizard}
          orgId={user.orgId}
          orgName={user.orgName}
          recruiterName={user.name}
          applications={apps}
          scheduleDayKey={wizardScheduleDayKey}
          initialApplicationId={selectedApplicationId}
          onCreated={() => {
            void loadList();
            void applicationService.getStatusHistory(selectedApplicationId).then(setHistory);
            void emailService.listByApplication(selectedApplicationId).then(setEmailThreads);
            void interviewEventService.listByApplication(selectedApplicationId).then((evs) => {
              setEventsByApp((prev) => ({ ...prev, [selectedApplicationId]: evs }));
            });
            setWizardScheduleDayKey(null);
          }}
        />
      )}
    </div>
  );
}
